import { ArrowRight, Check, Clock, Copy, Info, KeyRound, QrCode, Settings2, Star } from "lucide-react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import { useBrebReceivingKey } from "@/shared/hooks/useBrebReceivingKey";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";
import { formatLimitAmount } from "@/shared/utils/amountLimits";

import { BREB_KEY_SOURCE_ICON, BREB_KEY_SOURCE_NAME, BREB_KEY_TONE } from "./brebKeySources";
import { useBrebKeyRangeLabel } from "./useBrebKeyRangeLabel";

export interface BrebKeyEntryCardProps {
  organizationId?: string;
  /** Opens the full key view: the guided creation, or the key with its instructions. */
  onOpen: () => void;
  /** Adds a QR action to each ACTIVE key; the host decides where its QR opens. */
  onShowQr?: (keyId: string) => void;
  /** `primary`: the primary key and how many more there are. `list`: every key, one row each. */
  layout?: "primary" | "list";
  /** A line under the card, rendered only when the card itself is, so it never stands alone. */
  footnote?: string;
  className?: string;
}

// Renders nothing it cannot back up: no state to show yet, or no permission to create the key it would invite.
export function BrebKeyEntryCard({
  organizationId,
  onOpen,
  onShowQr,
  layout = "primary",
  footnote,
  className,
}: BrebKeyEntryCardProps) {
  const { can } = useAuth();
  const { state } = useBrebReceivingKey(organizationId);

  if (!state) return null;
  if (!state.key && !can("payments:write")) return null;
  if (
    !state.key &&
    (!state.eligibility.enabled ||
      !state.eligibility.rail_enabled ||
      (state.eligibility.verified && state.options.length === 0))
  )
    return null;

  return (
    <div className={cn("space-y-3", className)}>
      {state.key && layout === "list" ? (
        <BrebKeyQuickList keys={state.keys} onManage={onOpen} onShowQr={onShowQr} />
      ) : state.key ? (
        <BrebKeyEntryRow
          receivingKey={state.key}
          extraKeys={state.keys.length - 1}
          onOpen={onOpen}
          onShowQr={onShowQr ? () => onShowQr(state.key!.id) : undefined}
        />
      ) : (
        <CreateKeyCard onOpen={onOpen} />
      )}
      {footnote && <p className="text-center text-xs text-muted-foreground">{footnote}</p>}
    </div>
  );
}

export interface BrebKeyEntryRowProps {
  /** The primary key. */
  receivingKey: BrebReceivingKey;
  /** How many more keys the organization holds; the key view lists them. */
  extraKeys?: number;
  onOpen: () => void;
  onShowQr?: () => void;
}

export function BrebKeyEntryRow({ receivingKey, extraKeys = 0, onOpen, onShowQr }: BrebKeyEntryRowProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const { copiedValue, copy } = useCopyToClipboard(t("breb_key_copy_error"));
  const pending = receivingKey.status === "PENDING";
  const copied = copiedValue === receivingKey.key_value;
  const format = (value: string) => formatLimitAmount(value, "COP", activeLanguage);
  const min = pending ? null : receivingKey.min_amount;
  const max = pending ? null : receivingKey.max_amount;
  const bounds =
    min && max
      ? {
          range: t("amount_limit_hint_range", { min: format(min), max: format(max) }),
          rule: t("breb_key_bounds_rule_range", { min: format(min), max: format(max) }),
        }
      : min
        ? {
            range: t("amount_limit_hint_min", { min: format(min) }),
            rule: t("breb_key_bounds_rule_min", { min: format(min) }),
          }
        : max
          ? {
              range: t("amount_limit_hint_max", { max: format(max) }),
              rule: t("breb_key_bounds_rule_max", { max: format(max) }),
            }
          : null;

  return (
    // Wraps once the key has less than its basis: on a phone the three actions take their own line instead of
    // squeezing the key and its bounds into a narrow column.
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/25 bg-primary/5 p-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#00735C] text-white">
        {pending ? (
          <Clock className="size-4" aria-hidden="true" />
        ) : (
          <KeyRound className="size-4" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1 basis-48">
        <p className="text-xs text-muted-foreground">
          {pending
            ? t("breb_key_pending_title")
            : t(extraKeys > 0 ? "breb_key_primary_label" : "breb_key_label")}
        </p>
        <p className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono text-sm font-semibold text-foreground sm:text-base">
            {receivingKey.key_value}
          </span>
          {extraKeys > 0 && (
            <ColocaBadge variant="secondary" className="shrink-0">
              <span aria-hidden="true">{t("breb_key_entry_more", { count: extraKeys })}</span>
              <span className="sr-only">{t("breb_key_entry_more_label", { count: extraKeys })}</span>
            </ColocaBadge>
          )}
        </p>
        {bounds && (
          <div className="text-xs text-muted-foreground">
            {bounds.range.slice(0, bounds.range.lastIndexOf(" ") + 1)}
            {/* The icon travels with the last word, so a wrap never strands it on a line of its own. */}
            <span className="whitespace-nowrap">
              {bounds.range.slice(bounds.range.lastIndexOf(" ") + 1)}
              <ColocaPopover
                side="top"
                contentClassName="w-64"
                trigger={
                  <ColocaIconButton
                    size="xs"
                    variant="ghost"
                    className="ml-0.5 size-5 align-middle"
                    icon={Info}
                    aria-label={t("breb_key_bounds_rule_label")}
                  />
                }
              >
                <p className="text-sm">{bounds.rule}</p>
              </ColocaPopover>
            </span>
          </div>
        )}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        {!pending && (
          <ColocaTooltip
            triggerRender={
              <ColocaIconButton
                size="sm"
                icon={copied ? Check : Copy}
                aria-label={copied ? t("breb_key_copied") : t("breb_key_copy")}
                onClick={() => void copy(receivingKey.key_value, t("breb_key_copied"))}
              />
            }
          >
            {copied ? t("breb_key_copied") : t("breb_key_copy")}
          </ColocaTooltip>
        )}
        {onShowQr && receivingKey.status === "ACTIVE" && (
          <ColocaTooltip
            triggerRender={
              <ColocaIconButton
                size="sm"
                variant="ghost"
                icon={QrCode}
                aria-label={t("breb_key_show_qr")}
                onClick={onShowQr}
              />
            }
          >
            {t("breb_key_show_qr")}
          </ColocaTooltip>
        )}
        <ColocaTooltip
          triggerRender={
            <ColocaIconButton
              size="sm"
              variant="ghost"
              icon={ArrowRight}
              aria-label={t("breb_key_entry_cta_view")}
              onClick={onOpen}
            />
          }
        >
          {t("breb_key_entry_cta_view")}
        </ColocaTooltip>
      </div>
    </div>
  );
}

function CreateKeyCard({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslations("payins");
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-3 text-left transition-colors hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-primary"
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#00735C] text-white">
        <KeyRound className="size-4" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{t("breb_key_compact_create")}</p>
        <p className="text-xs text-muted-foreground">{t("breb_key_compact_create_hint")}</p>
      </div>
      <ArrowRight
        className="size-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
        aria-hidden="true"
      />
    </button>
  );
}

interface BrebKeyQuickListProps {
  keys: BrebReceivingKey[];
  onManage: () => void;
  onShowQr?: (keyId: string) => void;
}

function BrebKeyQuickList({ keys, onManage, onShowQr }: BrebKeyQuickListProps) {
  const { t } = useTranslations("payins");
  return (
    <section className="space-y-2.5">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{t("breb_key_quick_title")}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("breb_key_quick_hint")}</p>
        </div>
        <ColocaButton size="sm" variant="ghost" icon={Settings2} onClick={onManage} className="shrink-0">
          {t("breb_key_quick_manage")}
        </ColocaButton>
      </div>
      <ul className="divide-y divide-primary/15 overflow-hidden rounded-2xl border border-primary/25 bg-primary/5">
        {keys.map((key) => (
          <BrebKeyQuickRow
            key={key.id}
            receivingKey={key}
            onShowQr={onShowQr ? () => onShowQr(key.id) : undefined}
          />
        ))}
      </ul>
    </section>
  );
}

function BrebKeyQuickRow({
  receivingKey,
  onShowQr,
}: {
  receivingKey: BrebReceivingKey;
  onShowQr?: () => void;
}) {
  const { t } = useTranslations("payins");
  const { copiedValue, copy } = useCopyToClipboard(t("breb_key_copy_error"));
  const range = useBrebKeyRangeLabel(receivingKey);
  const pending = receivingKey.status === "PENDING";
  const copied = copiedValue === receivingKey.key_value;
  const Icon = BREB_KEY_SOURCE_ICON[receivingKey.source];

  return (
    <li className="flex items-center gap-3 p-3">
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full text-white",
          pending ? BREB_KEY_TONE.pending : BREB_KEY_TONE.live,
        )}
      >
        {pending ? (
          <Clock className="size-4" aria-hidden="true" />
        ) : (
          <Icon className="size-4" aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-sm font-semibold text-foreground">{receivingKey.key_value}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
          <span>{t(BREB_KEY_SOURCE_NAME[receivingKey.source])}</span>
          {receivingKey.is_primary && (
            <ColocaBadge variant="success" icon={Star}>
              {t("breb_key_primary_badge")}
            </ColocaBadge>
          )}
          {pending && (
            <ColocaBadge variant="secondary" icon={Clock}>
              {t("breb_key_pending_badge")}
            </ColocaBadge>
          )}
          {range && <span aria-hidden="true">·</span>}
          {range && <span>{range}</span>}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!pending && (
          <ColocaTooltip
            triggerRender={
              <ColocaIconButton
                size="sm"
                variant="ghost"
                icon={copied ? Check : Copy}
                aria-label={copied ? t("breb_key_copied") : t("breb_key_copy")}
                onClick={() => void copy(receivingKey.key_value, t("breb_key_copied"))}
              />
            }
          >
            {copied ? t("breb_key_copied") : t("breb_key_copy")}
          </ColocaTooltip>
        )}
        {onShowQr && receivingKey.status === "ACTIVE" && (
          <ColocaTooltip
            triggerRender={
              <ColocaIconButton
                size="sm"
                icon={QrCode}
                aria-label={t("breb_key_show_qr")}
                onClick={onShowQr}
              />
            }
          >
            {t("breb_key_show_qr")}
          </ColocaTooltip>
        )}
      </div>
    </li>
  );
}
